/**
 * **The one sentence at the right end of the tab bar** — KANON v3's `tabMeta`.
 *
 * The bar draws it and the TAB knows it: "1 – 30 sierpnia" is the range Trendy is showing,
 * "ostatni pomiar 9 sierpnia" is Pomiary's newest reading, "3 ostatnie rekordy" is what Rekordy
 * found. Only the mounted tab has any of them, and three of the six publish nothing — Przegląd
 * among them, since its own sentence came off the bar on 1 September 2026.
 *
 * ── Why a context rather than six props ───────────────────────────────────────────────────────
 * Passing `onMeta` down to each of six tabs would put the same prop, the same effect and the same
 * cleanup in six components — the shape this app calls shallow, because every new tab would
 * re-implement the wiring. One setter, published once, is the smaller interface: a tab says what
 * it has and knows nothing about where it is drawn.
 *
 * The setter is stable for the provider's life, so a tab that publishes does not re-render because
 * of this context, and a tab that publishes nothing pays nothing. Clearing on unmount is what
 * stops the previous tab's sentence standing over the next one for the frame before its read
 * lands.
 */

import { createContext, useContext, useEffect } from 'react';

const SetTabMetaContext = createContext<((text: string | null) => void) | null>(null);

export const TabMetaProvider = SetTabMetaContext.Provider;

/**
 * Publish this tab's line. Pass `null` while the read is in flight — the bar then draws nothing,
 * which is honest, rather than a stale sentence about the tab the trainer just left.
 */
export function usePublishTabMeta(text: string | null): void {
  const publish = useContext(SetTabMetaContext);
  useEffect(() => {
    if (publish === null) return undefined;
    publish(text);
    return () => {
      publish(null);
    };
  }, [publish, text]);
}
